import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import { useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ObjectSchema, object } from 'yup'

import { MessagesModel } from '@/Shared/domain'
import {
  checkboxSchema,
  nonEmptyStringSchema,
  useMetaResponseHandler,
} from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { openNavigationRoutes } from '@/Shared/ui/routers'
import { UserContract } from '@/User/application'
import { DeleteAccountDataForm, UserRepository } from '@/User/domain'
import { deleteAccountTranslation } from '@/User/ui/userTranslation'

interface IUseDeleteAccount {
  deleteAccount: (data: DeleteAccountDataForm) => Promise<boolean>
  form: UseFormReturn<DeleteAccountDataForm>
  errorMessage: string
}

const defaultValues: DeleteAccountDataForm = {
  reason: '',
  acceptConditions: false,
}

const formValidationScheme = (t: TFunction): ObjectSchema<DeleteAccountDataForm> =>
  object({
    reason: nonEmptyStringSchema(t, 400),
    acceptConditions: checkboxSchema(t)
      .test(
        'must-be-accepted',
        t(deleteAccountTranslation.modal.acceptConditions.error),
        value => value === true,
      )
      .required(),
  })

export const useDeleteAccount = (repository: UserRepository): IUseDeleteAccount => {
  const { startLoading, stopLoading } = useLoader()
  const { t } = useTranslation()
  const { handleMetaResponse } = useMetaResponseHandler()
  const navigate = useNavigate()
  const [errorMessage, setStateErrorMessage] = useState<string>('')

  const form = useForm<DeleteAccountDataForm>({
    resolver: yupResolver(formValidationScheme(t)),
    mode: 'onChange',
    defaultValues,
  })

  const deleteAccount = async (data: DeleteAccountDataForm): Promise<boolean> => {
    startLoading()

    const response = await UserContract(repository).deleteAccount(data)

    stopLoading()

    const valid = handleMetaResponse(response?.meta, form, {
      successMessage: t(deleteAccountTranslation.deletedSuccess),
    })

    if (valid) {
      form.reset({
        ...form.getValues(),
        reason: '',
        acceptConditions: false,
      })
      navigate(openNavigationRoutes.accountDeleted)
    } else {
      if (response?.meta?.messages.length > 0) {
        response?.meta?.messages.forEach((error: MessagesModel) => {
          if (error.value) {
            const errorOption = error.value === 'reason'
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            form.setError(errorOption as any, {
              type: 'custom',
              message: error.description,
            })
          } else {
            setStateErrorMessage(response?.meta?.messages[0].description)
          }
        })
      }
    }

    return valid
  }

  return { deleteAccount, form, errorMessage }
}
